import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, BookOpenIcon, MapIcon, BriefcaseIcon, BarChartIcon, BellIcon, SearchIcon, MenuIcon, XIcon, UserIcon, LogOutIcon } from 'lucide-react';
const navItems = [{
  path: '/dashboard',
  label: 'Overview',
  icon: LayoutDashboardIcon,
  exact: true
}, {
  path: '/dashboard/learning',
  label: 'Learning Hub',
  icon: BookOpenIcon
}, {
  path: '/dashboard/career',
  label: 'Career Path',
  icon: MapIcon
}, {
  path: '/dashboard/jobs',
  label: 'Careers',
  icon: BriefcaseIcon
}, {
  path: '/dashboard/impact',
  label: 'Impact',
  icon: BarChartIcon
}];
export function DashboardLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  return <div className="flex h-screen bg-surface-50 overflow-hidden" data-id="element-18">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence data-id="element-19">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-40 bg-surface-900/50 backdrop-blur-sm lg:hidden" onClick={closeMobileMenu} data-id="element-20" />}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-surface-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:flex lg:flex-col ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-21">
        <div className="flex items-center justify-between h-24 px-6 border-b border-surface-100" data-id="element-22">
          <NavLink to="/" className="flex items-center gap-2" data-id="element-23">
            <img src="https://cdn.magicpatterns.com/uploads/fBQoASXNwH9X9DPqUswNbF/Equity_Path_Logo.png" alt="EquityPath" className="h-20 w-auto object-contain" data-id="element-24" />
          </NavLink>
          <button className="lg:hidden text-surface-500" onClick={closeMobileMenu} data-id="element-25">
            <XIcon className="w-5 h-5" data-id="element-26" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-1" data-id="element-27">
          <div className="text-xs font-semibold text-surface-400 uppercase tracking-wider mb-4 px-2" data-id="element-28">
            Menu
          </div>
          {navItems.map(item => {
          const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
          return <NavLink key={item.path} to={item.path} onClick={closeMobileMenu} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors ${isActive ? 'bg-primary-50 text-primary-700' : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'}`} data-id="element-29">
                <item.icon className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-surface-400'}`} data-id="element-30" />
                {item.label}
              </NavLink>;
        })}
        </div>

        <div className="p-4 border-t border-surface-100" data-id="element-31">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-50 cursor-pointer transition-colors" data-id="element-32">
            <div className="w-10 h-10 rounded-full bg-surface-200 flex items-center justify-center overflow-hidden" data-id="element-33">
              <img src="https://cdn.magicpatterns.com/uploads/126AXCT6wfiSXDSoFCrDVP/ChatGPT_Image_Apr_4,_2026,_05_56_39_PM.png" alt="Marcus Johnson" className="w-full h-full object-cover object-top" data-id="element-34" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-35">
              <p className="text-sm font-medium text-surface-900 truncate" data-id="element-36">
                Marcus Johnson
              </p>
              <p className="text-xs text-surface-500 truncate" data-id="element-37">
                Learner Profile
              </p>
            </div>
            <LogOutIcon className="w-4 h-4 text-surface-400" data-id="element-38" />
          </div>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-39">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-surface-200 flex items-center justify-between px-4 sm:px-6 lg:px-8 z-10" data-id="element-40">
          <div className="flex items-center gap-4" data-id="element-41">
            <button className="lg:hidden text-surface-500 hover:text-surface-900" onClick={() => setIsMobileMenuOpen(true)} data-id="element-42">
              <MenuIcon className="w-6 h-6" data-id="element-43" />
            </button>

            <div className="hidden sm:flex items-center max-w-md w-full relative" data-id="element-44">
              <SearchIcon className="w-4 h-4 absolute left-3 text-surface-400" data-id="element-45" />
              <input type="text" placeholder="Search courses, jobs, or skills..." className="w-64 pl-10 pr-4 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all" data-id="element-46" />
            </div>
          </div>

          <div className="flex items-center gap-4" data-id="element-47">
            <button className="relative p-2 text-surface-400 hover:text-surface-600 transition-colors rounded-full hover:bg-surface-100" data-id="element-48">
              <BellIcon className="w-5 h-5" data-id="element-49" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" data-id="element-50"></span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-surface-50 p-4 sm:p-6 lg:p-8" data-id="element-51">
          <motion.div key={location.pathname} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.15
        }} className="max-w-7xl mx-auto h-full" data-id="element-52">
            <Outlet data-id="element-53" />
          </motion.div>
        </main>
      </div>
    </div>;
}